import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import locale from '@glu/locale';
import { withStyles } from '@glu/theming';
import { Button, PRIMARY, SECONDARY, ButtonToggleGroup, ButtonToggle } from '@glu/buttons-react';
import { CheckboxRadio } from '@glu/form-components';
import { PanelWrapper } from '@glu/grid-mobile-react';
import MobileGridContext from '../logic/MobileGridContext';

// TODO: check sortFieldId, and sortDirection and use gridContext instead of props
const SortPanel = ({ classes, closeDrawer, sortFieldId, sortDirection, onSave }) => {
  const gridContext = React.useContext(MobileGridContext);
  const [sortDirectionState, setSortDirectionState] = useState(sortDirection);
  const [sortFieldIdState, setSortFieldIdState] = useState(sortFieldId);
  const columns = gridContext.columnsWithoutActions.filter((col) => col.sortable !== false);

  const handleSave = useCallback(() => {
    onSave({ direction: sortDirectionState, field: sortFieldIdState });
  }, [sortDirectionState, sortFieldIdState, onSave]);

  const handleClose = useCallback(() => {
    setSortDirectionState(sortDirection);
    setSortFieldIdState(sortFieldId);
    closeDrawer();
  }, [sortDirection, sortFieldId, closeDrawer]);

  useEffect(() => {
    setSortDirectionState(sortDirection);
    setSortFieldIdState(sortFieldId);
  }, [sortDirection, sortFieldId]);

  return (
    <PanelWrapper
      title={locale.get('sort')}
      additionalTitleContent={
        <ButtonToggleGroup
          selectedButtonId={sortDirectionState}
          onButtonSelected={(id) => setSortDirectionState(id)}
        >
          <ButtonToggle id="asc">{locale.get('ascending')}</ButtonToggle>
          <ButtonToggle id="desc">{locale.get('descending')}</ButtonToggle>
        </ButtonToggleGroup>
      }
      footer={
        <>
          <Button
            variant={PRIMARY}
            type="button"
            text={locale.get('apply')}
            onClick={handleSave}
          />
          <Button
            variant={SECONDARY}
            type="button"
            text={locale.get('cancel')}
            onClick={handleClose}
          />
        </>
      }
    >
      {columns.map(({ field, headerName }) => (
        <CheckboxRadio
          key={field}
          htmlId={field}
          type="radio"
          name="sort"
          labelText={headerName}
          checked={field === sortFieldIdState}
          value={field}
          className={classes.radio}
          onChange={() => setSortFieldIdState(field)}
        />
      ))}
    </PanelWrapper>
  );
};

SortPanel.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  closeDrawer: PropTypes.func.isRequired,
  sortDirection: PropTypes.string,
  sortFieldId: PropTypes.string,
  onSave: PropTypes.func.isRequired,
};

SortPanel.defaultProps = {
  sortDirection: 'asc',
  sortFieldId: undefined,
};

const styles = ({ mobilegrid: { inputRadio } }) => ({
  radio: {
    margin: '1.5rem 0',
    cursor: 'pointer',
    '& input[type="radio"]': {
      '& + div svg': {
        background: inputRadio.checkmarkBackground,
        fill: inputRadio.checkmarkBorder,
        height: 16,
        width: 16,
      },
      '&:checked + div': {
        '& svg circle': {
          fill: inputRadio.checkmarkBackgroundChecked,
        },
      },
    },
    '& p': {
      fontFamily: inputRadio.fontFamily,
      fontWeight: inputRadio.fontWeight,
    },
  },
});

export default withStyles(styles)(SortPanel);
